import React from 'react';
import {
  Box,
  HStack,
  IconButton,
  Input,
  StackProps,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {CheckIcon, CloseIcon, EditIcon} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';
import {State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {ModeTarifaireSouhaite, Unite} from 'api-types/ffconnect';
import {CategorieDeTarif} from 'api-types/tolede';
import {TypographyProps} from '@chakra-ui/system';
import {toDecimalFr, useAffichagePrixTtc} from 'ff-common';
import {PrixTag} from '@/domains/catalogue/components/PrixTag';
import {EditionPrixState, usePriceEditor} from '@/hooks/panier/usePriceEditor';
import {AjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {ModeTarifaireToggle} from './ModeTarifaireToggle';
import {ResetPriceButton} from './ResetPriceButton';
import {PRICE_INPUT_PATTERN, PRICE_INPUT_TITLE} from '@/utils/price-validation';
import {PrixPostPromotionTooltip} from './PrixPostPromotionTooltip';
import {prixAfficheEstPromo} from './prix-affiche-est-promo';
import {useClient} from '@/haligator-factory';

/**
 * Bloc prix éditable (commercial uniquement).
 * Affiche le tag de catégorie, le prix unitaire et la TVA, avec édition inline au clic.
 * L'édition reste toujours en HT ; en lecture seule, le mode TTC client peut s'appliquer.
 */
export const PrixEditable: React.FC<{
  ligneDePanier?: State<LigneDePanierDesc>;
  /**
   * Prix à afficher, choisi par l'appelant selon l'écran : prix du catalogue hors panier
   * (cadencier, listes, saison, OC, UB, cards catalogue), prix de la ligne dans le panier
   * et le side panel.
   */
  prixUnitaire: number;
  /** Prix unitaire TTC serveur ; utilisé en lecture seule si affichage TTC client actif. */
  prixUnitaireTtc?: number;
  /**
   * Écran où le bloc est rendu, même valeur que celle passée à `usePrixCategorieTag`.
   */
  contexte: 'panier' | 'catalogue';
  uniteFacturation: Unite;
  tauxTVA: number | undefined;
  typePrix?: string;
  categorieDeTarif?: CategorieDeTarif;
  modeTarifaireSouhaite?: ModeTarifaireSouhaite;
  fontSizeMain?: TypographyProps['fontSize'];
  fontSizeSecondary?: TypographyProps['fontSize'];
  style?: StackProps;
  saisieAvantAjout?: boolean;
  ajouterAvecPrixSaisi?: AjouterAvecPrixSaisi;
  /** Requis hors panier pour charger `configuration-prix-article` (modale prix métier). */
  codeArticle?: string;
  /** Remonte le draft au parent pour le preview des marges. */
  onEditionPrixChange?: (state: EditionPrixState) => void;
  /** Prix affiché en promo, pour le tooltip hors panier. N'affiche pas de tag. */
  estPromo?: boolean;
}> = ({
  ligneDePanier,
  prixUnitaire,
  prixUnitaireTtc,
  contexte,
  uniteFacturation,
  tauxTVA,
  typePrix,
  categorieDeTarif,
  modeTarifaireSouhaite,
  fontSizeMain = 'lg',
  fontSizeSecondary = 'xs',
  style,
  saisieAvantAjout = false,
  ajouterAvecPrixSaisi,
  codeArticle,
  onEditionPrixChange,
  estPromo = false,
}) => {
  // Un prix saisi ou bloqué par le commercial prime partout ; sinon on affiche celui que
  // l'appelant a choisi. Hors panier, `prix_unitaire` de la ligne peut avoir été recalculé
  // par le contrôle Tolede à la date de livraison du panier (promotion qui ne la couvre pas) :
  // l'afficher au catalogue y montrerait un prix qui ne vaut que pour ce panier.
  const prixHtAffiche = !ligneDePanier
    ? prixUnitaire
    : contexte === 'catalogue'
      ? (ligneDePanier.data.prix_dero_souhaite ?? prixUnitaire)
      : prixUnitaire;
  const {t} = useTranslation();
  const affichagePrixTtc = useAffichagePrixTtc(useClient());
  const {
    isEditing,
    prixText,
    setPrixText,
    isDerogation,
    utilisateurDeroAutorisee,
    modifierPrixCommand,
    isPrixModifie,
    peutEditer,
    peutModifierPrix,
    handleStartEdit,
    handleSave,
    handleCancel,
    handleSupprimerPrix,
    handleKeyDown,
    handleToggleDerogation,
    isSavingHorsPanier,
    configHorsPanierEnChargement,
  } = usePriceEditor({
    ligneDePanier,
    saisieAvantAjout,
    ajouterAvecPrixSaisi,
    codeArticle,
    onEditionPrixChange,
  });

  const isSubmitting = modifierPrixCommand.isSubmitting || isSavingHorsPanier;
  const validerIndisponible = isSubmitting || configHorsPanierEnChargement;

  const typePrixRendTag = typePrix === 'promo' || typePrix === 'marche';
  const hasTag =
    !!(categorieDeTarif ?? modeTarifaireSouhaite) || typePrixRendTag;

  // Mode édition
  if (isEditing) {
    return (
      <VStack
        minW={peutModifierPrix ? '10.5rem' : '11em'}
        alignItems='start'
        gap='1'
        mt={{base: '15px', md: hasTag ? '-12px' : '15px'}}
        fontSize={fontSizeSecondary}
        {...style}
      >
        {hasTag && (
          <PrixTag
            typePrix={typePrix}
            categorieDeTarif={categorieDeTarif}
            modeTarifaireSouhaite={modeTarifaireSouhaite}
            styleProps={{mb: 0}}
          />
        )}
        <HStack
          spacing={0}
          bg='blue.main_transparency_10'
          borderRadius='8px'
          h='34px'
          p={1}
        >
          <Input
            value={prixText}
            onChange={(e) => {
              setPrixText(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            type='text'
            pattern={PRICE_INPUT_PATTERN}
            title={PRICE_INPUT_TITLE}
            size='sm'
            width='80px'
            bg='white'
            borderRadius='6px'
            fontSize={fontSizeMain}
            fontWeight='700'
            autoFocus
            isDisabled={isSubmitting}
          />
          <IconButton
            icon={<CheckIcon />}
            aria-label={t('global.valider')}
            size='xs'
            colorScheme='green'
            variant='ghost'
            onClick={handleSave}
            isLoading={validerIndisponible}
            isDisabled={configHorsPanierEnChargement}
          />
          <IconButton
            icon={<CloseIcon boxSize={2.5} />}
            aria-label={t('global.annuler')}
            size='xs'
            variant='ghost'
            color='gray.dark'
            onClick={handleCancel}
            isDisabled={isSubmitting}
          />
        </HStack>
        {/* Toggle pour choisir le mode tarifaire : one_shot ou dero */}
        <ModeTarifaireToggle
          isDerogation={isDerogation}
          onChange={handleToggleDerogation}
          isHidden={!utilisateurDeroAutorisee}
          isDisabled={isSubmitting}
        />
      </VStack>
    );
  }

  // Lecture : TTC pour le client et la Vue Client, HT pour le commercial (règle portée par useAffichagePrixTtc)
  const afficherTtc =
    affichagePrixTtc &&
    prixUnitaireTtc !== undefined &&
    // En catalogue, un prix dérogé HT saisi par le commercial n'a pas de TTC dédié
    !(
      contexte === 'catalogue' && ligneDePanier?.data.prix_dero_souhaite != null
    );
  const prixAffiche = afficherTtc ? prixUnitaireTtc : prixHtAffiche;
  const labelTaxe = afficherTtc
    ? t('ligne_produit.TTC')
    : t('ligne_produit.HT');

  // Mode lecture
  const prixLine = (rightAddon?: React.ReactNode) => (
    <PrixPostPromotionTooltip
      ligneDePanier={ligneDePanier}
      codeArticle={codeArticle}
      estPromo={estPromo || prixAfficheEstPromo(typePrix, categorieDeTarif)}
      shouldShow={true}
    >
      <HStack
        gap='0.5'
        alignItems='center'
        width='100%'
      >
        <Text
          textColor='black.text'
          fontSize={fontSizeMain}
          fontWeight='700'
        >
          {toDecimalFr(prixAffiche, 2, 3)}
        </Text>
        <Text>€</Text>
        <Text>
          {labelTaxe}/{t(`unite.${uniteFacturation}`, {count: 1})}
        </Text>
        {rightAddon}
      </HStack>
    </PrixPostPromotionTooltip>
  );

  const tvaLine = !afficherTtc && tauxTVA !== undefined && (
    <Text color='gray.dark'>{t('ligne_produit.tva_percent', {tauxTVA})}</Text>
  );

  return (
    <VStack
      minW={peutModifierPrix ? '10.5rem' : '11em'}
      alignItems='start'
      gap='0'
      mt={{base: '15px', md: hasTag ? '-12px' : '15px'}}
      fontSize={fontSizeSecondary}
      {...style}
    >
      {hasTag && (
        <PrixTag
          typePrix={typePrix}
          categorieDeTarif={categorieDeTarif}
          modeTarifaireSouhaite={modeTarifaireSouhaite}
          styleProps={{mb: 1}}
        />
      )}
      {peutEditer ? (
        <>
          <Box
            role='group'
            width='100%'
            cursor='pointer'
            onClick={() => {
              handleStartEdit(prixHtAffiche);
            }}
            borderRadius='8px'
            px={1}
            py={0.5}
            mx={-1}
            opacity={isSubmitting ? 0.5 : 1}
            _hover={{bg: 'blue.main_transparency_10'}}
            transition='background 0.15s'
          >
            {prixLine(
              // Bloc icônes fixe : réserve toujours l'espace de la croix rouge (via visibility),
              // pour que la largeur du bloc prix soit identique que la ligne soit modifiée ou non.
              <HStack
                ml='auto'
                h='30px'
                opacity={0}
                _groupHover={{opacity: 1}}
                transition='opacity 0.2s'
                spacing={0}
                alignItems='center'
              >
                <Tooltip
                  label={t('global.modifier')}
                  placement='top'
                >
                  <Box
                    color='blue.main'
                    display='flex'
                    alignItems='center'
                  >
                    <EditIcon boxSize={3} />
                  </Box>
                </Tooltip>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  display='flex'
                  alignItems='center'
                  visibility={isPrixModifie ? 'visible' : 'hidden'}
                >
                  <ResetPriceButton
                    onReset={handleSupprimerPrix}
                    isLoading={isSubmitting}
                  />
                </Box>
              </HStack>,
            )}
          </Box>
          {tvaLine}
        </>
      ) : (
        <>
          {prixLine(
            // Spacer invisible pour aligner ce bloc (cas M/N/S non éditable)
            // avec ceux qui contiennent les icônes Edit + Reset (peutEditer=true).
            peutModifierPrix ? (
              <HStack
                ml='auto'
                spacing={0}
                alignItems='center'
                visibility='hidden'
              >
                <Box
                  display='flex'
                  alignItems='center'
                >
                  <EditIcon boxSize={4} />
                </Box>
                <Box
                  mr={1}
                  w='20px'
                  h='20px'
                />
              </HStack>
            ) : null,
          )}
          {tvaLine}
        </>
      )}
    </VStack>
  );
};
